// src/pages/BookOfBusiness.jsx
// Book of Business analytics — dark-theme, 6-tab analytics command center
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, AreaChart, Area, LineChart, Line,
  Treemap, PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import {
  TrendingUp, TrendingDown, Users, DollarSign, Activity,
  AlertTriangle, Shield, Target, ChevronRight, RefreshCw,
  BookOpen, BarChart3, PieChart as PieIcon, Zap, Calculator,
  ArrowLeft, Plus, Check, Info, Star, Award, Package
} from 'lucide-react';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ─── Design system ─────────────────────────────────────────────────────────
const CHART_COLORS = ['#FFB800', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#F59E0B', '#06B6D4', '#6B7280'];
const TABS = [
  { id: 'overview',   label: 'Book Overview',         icon: BookOpen },
  { id: 'carrier',    label: 'Carrier Analysis',       icon: PieIcon },
  { id: 'demo',       label: 'Client Demographics',    icon: Users },
  { id: 'growth',     label: 'Growth & Trends',        icon: TrendingUp },
  { id: 'crosssell',  label: 'Cross-Sell Intelligence',icon: Zap },
  { id: 'valuation',  label: 'Book Valuation',         icon: Calculator },
];

// ─── Utility helpers ────────────────────────────────────────────────────────
function fmt$(n) {
  if (n == null) return '$—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}
function fmtN(n) {
  return (n ?? 0).toLocaleString();
}
function healthColor(score) {
  if (score >= 75) return '#10B981';
  if (score >= 50) return '#FFB800';
  return '#EF4444';
}
function healthLabel(score) {
  if (score >= 75) return 'Strong';
  if (score >= 50) return 'Needs Work';
  return 'At Risk';
}
function hhiLabel(hhi) {
  if (hhi > 0.40) return { label: 'High Risk', color: '#EF4444' };
  if (hhi > 0.25) return { label: 'Moderate', color: '#FFB800' };
  return { label: 'Healthy', color: '#10B981' };
}

// ─── Shared card shell ──────────────────────────────────────────────────────
function Card({ children, className = '' }) {
  return (
    <div className={`bg-gray-900 border border-gray-800 rounded-lg p-4 ${className}`}>
      {children}
    </div>
  );
}

// ─── Loading skeleton ───────────────────────────────────────────────────────
function Skeleton({ h = 'h-6', w = 'w-full' }) {
  return <div className={`${h} ${w} bg-gray-800 rounded animate-pulse`} />;
}

// ─── Book Health Score gauge ────────────────────────────────────────────────
function HealthGauge({ score, loading }) {
  const color = healthColor(score || 0);
  const label = healthLabel(score || 0);
  const circumference = 2 * Math.PI * 36;
  const dash = loading ? 0 : (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-24 h-24">
        <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
          <circle cx="40" cy="40" r="36" fill="none" stroke="#1F2937" strokeWidth="8" />
          <circle
            cx="40" cy="40" r="36" fill="none"
            stroke={color} strokeWidth="8"
            strokeDasharray={`${dash} ${circumference}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 1s ease-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-white tabular-nums">{loading ? '—' : score}</span>
        </div>
      </div>
      <span className="text-xs mt-1 font-medium" style={{ color }}>{label}</span>
      <span className="text-xs text-gray-500">Health Score</span>
    </div>
  );
}

// ─── KPI Capsule ────────────────────────────────────────────────────────────
function KpiCapsule({ label, value, sub, loading, accent }) {
  return (
    <div className="flex flex-col gap-0.5 px-4 py-2 bg-gray-900 border border-gray-800 rounded-lg min-w-[130px]">
      <span className="text-xs text-gray-500 whitespace-nowrap">{label}</span>
      {loading
        ? <Skeleton h="h-7" w="w-20" />
        : <span className={`text-2xl font-bold tabular-nums ${accent ? 'text-amber-400' : 'text-white'}`}>{value}</span>
      }
      {sub && <span className="text-xs text-gray-500">{sub}</span>}
    </div>
  );
}

// ─── Custom Treemap content ──────────────────────────────────────────────────
function TreemapCell(props) {
  const { x, y, width, height, name, value, pct, alertLevel } = props;
  if (width < 30 || height < 20) return null;
  const borderColor = alertLevel === 'critical' ? '#EF4444'
    : alertLevel === 'warning' ? '#F59E0B'
    : alertLevel === 'watch'   ? '#3B82F6'
    : '#374151';
  return (
    <g>
      <rect x={x} y={y} width={width} height={height}
        fill={props.fill} stroke={borderColor} strokeWidth={alertLevel !== 'none' ? 2 : 1} rx={4} />
      {width > 60 && height > 30 && (
        <>
          <text x={x + 8} y={y + 18} fill="#fff" fontSize={11} fontWeight="600" dominantBaseline="middle">
            {name?.length > 16 ? name.slice(0, 15) + '…' : name}
          </text>
          {height > 40 && (
            <text x={x + 8} y={y + 34} fill="#9CA3AF" fontSize={10}>
              {value} policies · {pct}%
            </text>
          )}
        </>
      )}
    </g>
  );
}

// ─── Sub-score row ───────────────────────────────────────────────────────────
function SubScoreRow({ label, score, weight }) {
  const color = healthColor(score || 0);
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-400 w-40 shrink-0">{label}</span>
      <div className="flex-1 bg-gray-800 rounded-full h-2">
        <div className="h-2 rounded-full transition-all duration-700"
          style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-mono text-white w-8 text-right">{score}</span>
      <span className="text-xs text-gray-600 w-8">{weight}</span>
    </div>
  );
}

// ─── Alert badge ─────────────────────────────────────────────────────────────
function AlertBadge({ level }) {
  if (!level || level === 'none') return null;
  const map = {
    critical: 'bg-red-900/60 text-red-400 border-red-800',
    warning:  'bg-yellow-900/60 text-yellow-400 border-yellow-800',
    watch:    'bg-blue-900/60 text-blue-400 border-blue-800',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${map[level] || ''}`}>
      {level.charAt(0).toUpperCase() + level.slice(1)}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB 1: BOOK OVERVIEW
// ────────────────────────────────────────────────────────────────────────────
function TabOverview({ overview, carriers, planMix, t65 }) {
  const loading = !overview;

  // Build treemap data
  const treemapData = (carriers?.carriers || []).slice(0, 12).map((c, i) => ({
    name:       c.carrier,
    size:       c.policyCount,
    value:      c.policyCount,
    pct:        c.pct,
    alertLevel: c.alertLevel,
    fill:       CHART_COLORS[i % CHART_COLORS.length] + 'CC',
  }));

  // Build plan type stacked bar
  const planTypes = planMix?.planTypes || [];
  const quarters  = planMix?.quarters  || [];

  return (
    <div className="space-y-4">
      {/* Row 1: Treemap + Plan Mix */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Carrier Treemap */}
        <Card className="lg:col-span-3">
          <h3 className="text-sm font-semibold text-gray-300 mb-3">Carrier Distribution</h3>
          {loading || treemapData.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-gray-600 text-sm">
              {loading ? <Skeleton h="h-48" /> : 'No carrier data yet. Add clients with carriers.'}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <Treemap data={treemapData} dataKey="size" content={<TreemapCell />} />
            </ResponsiveContainer>
          )}
        </Card>

        {/* Plan Type Stacked Bar */}
        <Card className="lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-300 mb-3">Plan Type Mix (Quarterly)</h3>
          {loading || quarters.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-gray-600 text-sm">
              {loading ? <Skeleton h="h-48" /> : 'No quarterly data yet.'}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={quarters} margin={{ top: 4, right: 8, bottom: 16, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <XAxis dataKey="quarter" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                <YAxis tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8 }} labelStyle={{ color: '#F9FAFB' }} />
                {planTypes.slice(0, 5).map((pt, i) => (
                  <Bar key={pt} dataKey={pt} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} radius={i === planTypes.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                ))}
                <Legend wrapperStyle={{ color: '#9CA3AF', fontSize: 11 }} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Row 2: T65 summary + Cross-sell callout + Health sub-scores */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* T65 Pipeline Cards */}
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <Target size={16} className="text-amber-400" />
            <h3 className="text-sm font-semibold text-gray-300">T65 Pipeline</h3>
          </div>
          {loading ? (
            <div className="space-y-2"><Skeleton /><Skeleton /><Skeleton /></div>
          ) : (
            <div className="space-y-2">
              {[
                { label: 'Next 90 days',  count: t65?.summary?.next90  || 0, rev: t65?.conversionRevenue?.next90  || 0 },
                { label: 'Next 6 months', count: t65?.summary?.next180 || 0, rev: t65?.conversionRevenue?.next180 || 0 },
                { label: 'Next 12 months',count: t65?.summary?.next365 || 0, rev: t65?.conversionRevenue?.next365 || 0 },
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between p-2 bg-gray-800 rounded-lg">
                  <div>
                    <div className="text-xs text-gray-400">{item.label}</div>
                    <div className="text-lg font-bold text-white tabular-nums">{fmtN(item.count)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Est. revenue</div>
                    <div className="text-sm font-semibold text-amber-400">{fmt$(item.rev)}</div>
                  </div>
                </div>
              ))}
              <p className="text-xs text-gray-600 mt-1">Based on recently enrolled clients. 52% avg. conversion.</p>
            </div>
          )}
        </Card>

        {/* Cross-sell callout */}
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <Zap size={16} className="text-amber-400" />
            <h3 className="text-sm font-semibold text-gray-300">Cross-Sell Opportunity</h3>
          </div>
          {loading ? (
            <div className="space-y-2"><Skeleton /><Skeleton /></div>
          ) : (
            <div className="space-y-3">
              <div className="p-3 bg-amber-400/10 border border-amber-400/20 rounded-lg text-center">
                <div className="text-3xl font-bold text-amber-400 tabular-nums">
                  {fmt$(overview?.kpis ? Math.round((overview.kpis.totalPolicies * 0.35) * 200) : 0)}
                </div>
                <div className="text-xs text-gray-400 mt-1">Estimated annual potential</div>
              </div>
              <div className="text-xs text-gray-500 leading-relaxed">
                Clients with a single policy have 15%+ churn rate. Clients with 1.8+ policies/client have 95% retention. Target: {overview?.kpis?.policiesPerClient?.toFixed(2) || '—'} current ratio.
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-800 rounded-full h-2">
                  <div className="h-2 rounded-full bg-amber-400"
                    style={{ width: `${Math.min(100, ((overview?.kpis?.policiesPerClient || 0) / 1.8) * 100)}%` }} />
                </div>
                <span className="text-xs text-gray-400">{overview?.kpis?.policiesPerClient?.toFixed(2) || '0'} / 1.8 target</span>
              </div>
            </div>
          )}
        </Card>

        {/* Health Sub-scores */}
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <Activity size={16} className="text-amber-400" />
            <h3 className="text-sm font-semibold text-gray-300">Health Score Breakdown</h3>
          </div>
          {loading ? (
            <div className="space-y-2">{[...Array(7)].map((_, i) => <Skeleton key={i} />)}</div>
          ) : (
            <div className="space-y-2">
              <SubScoreRow label="Carrier Diversification" score={overview?.subScores?.carrierDiversification || 0} weight="20%" />
              <SubScoreRow label="Policies/Client"         score={overview?.subScores?.policiesPerClient     || 0} weight="20%" />
              <SubScoreRow label="T65 Pipeline Density"    score={overview?.subScores?.t65Density            || 0} weight="15%" />
              <SubScoreRow label="Renewal Rate"            score={overview?.subScores?.renewalRate           || 0} weight="15%" />
              <SubScoreRow label="90-Day Growth"           score={overview?.subScores?.growth90d             || 0} weight="15%" />
              <SubScoreRow label="Commission Maturity"     score={overview?.subScores?.commissionMaturity    || 0} weight="10%" />
              <SubScoreRow label="Avg Star Rating"         score={overview?.subScores?.starRating            || 0} weight="5%"  />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB 2: CARRIER ANALYSIS
// ────────────────────────────────────────────────────────────────────────────
function TabCarrier({ carriers }) {
  const [simulateCarrier, setSimulateCarrier] = useState('');
  const [simResult, setSimResult] = useState(null);
  const [loadingSim, setLoadingSim] = useState(false);
  const [sortKey, setSortKey] = useState('pct');
  const [sortDir, setSortDir] = useState(-1);

  const loading = !carriers;

  const runSimulation = useCallback(async (carrier) => {
    if (!carrier) return;
    setLoadingSim(true);
    try {
      const res = await fetch(`${BASE}/api/bob/carrier-concentration?simulate_exit=${encodeURIComponent(carrier)}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setSimResult(json.data.simulationResult);
    } catch (_) {}
    setLoadingSim(false);
  }, []);

  const sorted = [...(carriers?.carriers || [])].sort((a, b) =>
    sortDir * ((b[sortKey] || 0) - (a[sortKey] || 0))
  );

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d * -1);
    else { setSortKey(key); setSortDir(-1); }
  };

  const hhiInfo = carriers ? hhiLabel(carriers.hhi) : { label: '—', color: '#9CA3AF' };

  return (
    <div className="space-y-4">
      {/* HHI Banner */}
      {!loading && carriers.hhi > 0.25 && (
        <div className="flex items-start gap-3 p-3 rounded-lg border"
          style={{ backgroundColor: `${hhiInfo.color}15`, borderColor: `${hhiInfo.color}40` }}>
          <AlertTriangle size={16} style={{ color: hhiInfo.color, flexShrink: 0, marginTop: 2 }} />
          <div>
            <span className="text-sm font-semibold" style={{ color: hhiInfo.color }}>
              Concentration Alert — HHI: {carriers.hhi.toFixed(3)}
            </span>
            <p className="text-xs text-gray-400 mt-0.5">
              Your book is {hhiInfo.label.toLowerCase()}. A single carrier exit could affect a significant portion of your revenue. Consider diversifying across additional carriers.
            </p>
          </div>
        </div>
      )}

      {/* Carrier Treemap (large) */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-300">Carrier Concentration Map</h3>
          <div className="flex items-center gap-2 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-900/60 border border-red-800 inline-block" />Critical Alert</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-900/60 border border-yellow-800 inline-block" />Warning</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-900/60 border border-blue-800 inline-block" />Watch</span>
          </div>
        </div>
        {loading || !carriers?.carriers?.length ? (
          <div className="h-52 flex items-center justify-center text-gray-600 text-sm">
            {loading ? <Skeleton h="h-48" /> : 'No carrier data. Add clients with carriers to see concentration.'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <Treemap
              data={carriers.carriers.slice(0, 12).map((c, i) => ({
                name: c.carrier, size: c.policyCount, value: c.policyCount,
                pct: c.pct, alertLevel: c.alertLevel,
                fill: CHART_COLORS[i % CHART_COLORS.length] + 'CC',
              }))}
              dataKey="size"
              content={<TreemapCell />}
            />
          </ResponsiveContainer>
        )}
      </Card>

      {/* Simulate Exit Tool */}
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <Zap size={15} className="text-amber-400" />
          <h3 className="text-sm font-semibold text-gray-300">Simulate Carrier Exit</h3>
        </div>
        <div className="flex gap-2 mb-3">
          <select
            value={simulateCarrier}
            onChange={e => { setSimulateCarrier(e.target.value); setSimResult(null); }}
            className="flex-1 bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400"
          >
            <option value="">Select a carrier to simulate exit…</option>
            {(carriers?.carriers || []).map(c => (
              <option key={c.carrier} value={c.carrier}>{c.carrier} ({c.policyCount} policies, {c.pct}%)</option>
            ))}
          </select>
          <button
            onClick={() => runSimulation(simulateCarrier)}
            disabled={!simulateCarrier || loadingSim}
            className="px-4 py-2 bg-amber-400 text-gray-950 text-sm font-semibold rounded-lg disabled:opacity-40 hover:bg-amber-300 transition-colors"
          >
            {loadingSim ? <RefreshCw size={14} className="animate-spin" /> : 'Simulate'}
          </button>
        </div>
        {simResult && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Affected Clients', value: fmtN(simResult.affectedClients), color: 'text-red-400' },
              { label: '% of Book',        value: `${simResult.pctOfBook}%`,        color: 'text-red-400' },
              { label: 'Lost Annual Comm', value: fmt$(simResult.lostAnnualComm),   color: 'text-red-400' },
              { label: 'Re-enroll Upside', value: fmt$(simResult.potentialNewComm), color: 'text-green-400' },
            ].map(m => (
              <div key={m.label} className="p-3 bg-gray-800 rounded-lg text-center">
                <div className={`text-xl font-bold tabular-nums ${m.color}`}>{m.value}</div>
                <div className="text-xs text-gray-500 mt-1">{m.label}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Carrier Table */}
      <Card>
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Carrier Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs border-b border-gray-800">
                {[
                  { key: 'carrier',         label: 'Carrier' },
                  { key: 'policyCount',     label: 'Policies' },
                  { key: 'pct',             label: '% Book' },
                  { key: 'annualCommission',label: 'Est. Annual Comm.' },
                  { key: 'starRating',      label: 'Star Rating' },
                  { key: 'alertLevel',      label: 'Alert' },
                ].map(col => (
                  <th key={col.key}
                    className="text-left py-2 px-2 cursor-pointer hover:text-gray-300 transition-colors"
                    onClick={() => toggleSort(col.key)}>
                    {col.label}
                    {sortKey === col.key && <span className="ml-1">{sortDir === -1 ? '↓' : '↑'}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-gray-800">
                    {[...Array(6)].map((_, j) => <td key={j} className="py-2 px-2"><Skeleton h="h-4" /></td>)}
                  </tr>
                ))
                : sorted.map(c => (
                  <tr key={c.carrier} className="border-b border-gray-800/50 hover:bg-gray-800/40 transition-colors">
                    <td className="py-2 px-2 font-medium text-white">{c.carrier}</td>
                    <td className="py-2 px-2 tabular-nums text-gray-300">{fmtN(c.policyCount)}</td>
                    <td className="py-2 px-2 tabular-nums">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-800 rounded-full h-1.5">
                          <div className="h-1.5 rounded-full bg-amber-400" style={{ width: `${Math.min(100, c.pct)}%` }} />
                        </div>
                        <span className="text-gray-300">{c.pct}%</span>
                      </div>
                    </td>
                    <td className="py-2 px-2 tabular-nums text-gray-300">{fmt$(c.annualCommission)}</td>
                    <td className="py-2 px-2">
                      {c.starRating
                        ? <span className="flex items-center gap-1 text-amber-400"><Star size={12} fill="currentColor" />{c.starRating}</span>
                        : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="py-2 px-2"><AlertBadge level={c.alertLevel} /></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
        {!loading && (
          <div className="mt-3 pt-3 border-t border-gray-800 flex items-center gap-4 text-xs text-gray-500">
            <span>HHI: <strong className="text-white">{carriers?.hhi?.toFixed(3)}</strong></span>
            <span style={{ color: hhiInfo.color }}>{hhiInfo.label} Concentration</span>
            <span className="text-gray-600">(HHI &lt;0.15 = healthy, &gt;0.25 = moderate, &gt;0.40 = high)</span>
          </div>
        )}
      </Card>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB 3: CLIENT DEMOGRAPHICS
// ────────────────────────────────────────────────────────────────────────────
function TabDemographics({ demo, t65 }) {
  const loading = !demo;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Plan Type Mix */}
        <Card>
          <h3 className="text-sm font-semibold text-gray-300 mb-3">Plan Type Distribution</h3>
          {loading ? <Skeleton h="h-48" /> : (
            demo.planTypeMix?.length === 0 ? (
              <p className="text-gray-600 text-sm py-8 text-center">No plan type data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={demo.planTypeMix?.slice(0, 8) || []}
                  layout="vertical"
                  margin={{ top: 4, right: 40, bottom: 4, left: 72 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" horizontal={false} />
                  <XAxis type="number" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                  <YAxis dataKey="plan_type" type="category" tick={{ fill: '#D1D5DB', fontSize: 11 }} width={70} />
                  <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8 }} />
                  <Bar dataKey="cnt" name="Clients" radius={[0, 4, 4, 0]}>
                    {(demo.planTypeMix || []).map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )
          )}
        </Card>

        {/* Client Tenure Bands */}
        <Card>
          <h3 className="text-sm font-semibold text-gray-300 mb-3">Client Tenure (Enrollment Age)</h3>
          {loading ? <Skeleton h="h-48" /> : (
            <div className="space-y-3 pt-2">
              {(demo.tenureBands || []).map((band, i) => {
                const max = Math.max(...(demo.tenureBands || []).map(b => parseInt(b.cnt) || 0)) || 1;
                return (
                  <div key={band.tenure_band} className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-300">{band.tenure_band}</span>
                      <span className="text-amber-400 font-semibold tabular-nums">{fmtN(band.cnt)}</span>
                    </div>
                    <div className="bg-gray-800 rounded-full h-2">
                      <div className="h-2 rounded-full transition-all duration-700"
                        style={{ width: `${(parseInt(band.cnt) / max) * 100}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                    </div>
                  </div>
                );
              })}
              {!loading && (demo.tenureBands || []).length === 0 && (
                <p className="text-gray-600 text-sm text-center py-4">No enrollment date data.</p>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Geographic Distribution */}
      <Card>
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Geographic Distribution</h3>
        {loading ? <Skeleton h="h-48" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs border-b border-gray-800">
                  <th className="text-left py-2 px-2">State</th>
                  <th className="text-left py-2 px-2">Clients</th>
                  <th className="text-left py-2 px-2">% of Book</th>
                  <th className="text-left py-2 px-2">Carriers</th>
                </tr>
              </thead>
              <tbody>
                {(demo.geographic || []).map(g => {
                  const total = (demo.geographic || []).reduce((s, r) => s + parseInt(r.cnt), 0) || 1;
                  const pct = ((parseInt(g.cnt) / total) * 100).toFixed(1);
                  return (
                    <tr key={g.state} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                      <td className="py-2 px-2 font-medium text-white">{g.state}</td>
                      <td className="py-2 px-2 tabular-nums text-gray-300">{fmtN(g.cnt)}</td>
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-gray-800 rounded-full h-1.5">
                            <div className="h-1.5 rounded-full bg-amber-400" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-gray-400">{pct}%</span>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-gray-400">{g.carrier_count}</td>
                    </tr>
                  );
                })}
                {(demo.geographic || []).length === 0 && (
                  <tr><td colSpan={4} className="py-8 text-center text-gray-600">No state data. Add state to client records.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* T65 by Month */}
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <Target size={15} className="text-amber-400" />
          <h3 className="text-sm font-semibold text-gray-300">Recent Enrollment Pipeline (T65 Proxy)</h3>
        </div>
        {!t65 ? <Skeleton h="h-36" /> : (
          t65.byMonth?.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-6">No recent enrollments found.</p>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={t65.byMonth} margin={{ top: 4, right: 8, bottom: 16, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <XAxis dataKey="month" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                <YAxis tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8 }} />
                <Bar dataKey="count" name="Enrollments" fill="#FFB800" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )
        )}
      </Card>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB 4: GROWTH & TRENDS
// ────────────────────────────────────────────────────────────────────────────
function TabGrowth({ growth }) {
  const loading = !growth;

  return (
    <div className="space-y-4">
      {/* Net Book Growth Area Chart */}
      <Card>
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Net Book Growth (Monthly)</h3>
        {loading ? <Skeleton h="h-52" /> : (
          growth.monthly?.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-16">No monthly data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={growth.monthly} margin={{ top: 4, right: 8, bottom: 16, left: -16 }}>
                <defs>
                  <linearGradient id="newGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="lostGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#EF4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#FFB800" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#FFB800" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <XAxis dataKey="month" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                <YAxis tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8 }} />
                <Legend wrapperStyle={{ color: '#9CA3AF', fontSize: 11 }} />
                <Area type="monotone" dataKey="runningTotal" name="Total Book" stroke="#FFB800" fill="url(#totalGrad)" strokeWidth={2} dot={false} />
                <Area type="monotone" dataKey="newPolicies"  name="New"   stroke="#10B981" fill="url(#newGrad)"   strokeWidth={1.5} dot={false} />
                <Area type="monotone" dataKey="lostPolicies" name="Lost"  stroke="#EF4444" fill="url(#lostGrad)"  strokeWidth={1.5} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          )
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Cohort Retention Table */}
        <Card>
          <h3 className="text-sm font-semibold text-gray-300 mb-3">Cohort Retention</h3>
          {loading ? <Skeleton h="h-48" /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 text-xs border-b border-gray-800">
                    <th className="text-left py-2 px-2">Cohort</th>
                    <th className="text-left py-2 px-2">Enrolled</th>
                    <th className="text-left py-2 px-2">Active</th>
                    <th className="text-left py-2 px-2">Retention</th>
                  </tr>
                </thead>
                <tbody>
                  {(growth.cohorts || []).map(c => {
                    const color = c.retentionRate >= 90 ? '#10B981' : c.retentionRate >= 80 ? '#FFB800' : '#EF4444';
                    return (
                      <tr key={c.cohort} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                        <td className="py-2 px-2 text-gray-300 font-mono text-xs">{c.cohort}</td>
                        <td className="py-2 px-2 tabular-nums text-gray-400">{c.total}</td>
                        <td className="py-2 px-2 tabular-nums text-gray-400">{c.active}</td>
                        <td className="py-2 px-2">
                          <span className="font-semibold tabular-nums" style={{ color }}>{c.retentionRate}%</span>
                        </td>
                      </tr>
                    );
                  })}
                  {(growth.cohorts || []).length === 0 && (
                    <tr><td colSpan={4} className="py-6 text-center text-gray-600 text-sm">No cohort data yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Seasonal Enrollment Pattern */}
        <Card>
          <h3 className="text-sm font-semibold text-gray-300 mb-3">Seasonal Enrollment Pattern</h3>
          {loading ? <Skeleton h="h-48" /> : (
            growth.seasonal?.length === 0 ? (
              <p className="text-gray-600 text-sm text-center py-8">No seasonal data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={growth.seasonal} margin={{ top: 4, right: 8, bottom: 16, left: -16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                  <XAxis dataKey="month_name" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8 }} />
                  <Bar dataKey="count" name="Enrollments" radius={[4, 4, 0, 0]}>
                    {(growth.seasonal || []).map((entry, i) => {
                      // Highlight AEP (Oct-Dec = months 10-12) and OEP (Jan-Mar = 1-3)
                      const mo = entry.cal_month;
                      const isAEP = mo >= 10;
                      const isOEP = mo <= 3;
                      return <Cell key={i} fill={isAEP ? '#FFB800' : isOEP ? '#3B82F6' : '#4B5563'} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )
          )}
          {!loading && growth.seasonal?.length > 0 && (
            <div className="flex gap-4 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="w-3 h-2 bg-amber-400 rounded-sm inline-block" />AEP (Oct–Dec)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 bg-blue-500 rounded-sm inline-block" />OEP (Jan–Mar)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 bg-gray-600 rounded-sm inline-block" />Off-season</span>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB 5: CROSS-SELL INTELLIGENCE
// ────────────────────────────────────────────────────────────────────────────
function TabCrossSell({ crossSell, onTaskCreated }) {
  const loading = !crossSell;
  const [creating, setCreating] = useState({});
  const [created, setCreated]   = useState({});
  const [minScore, setMinScore] = useState(50);

  const createTask = useCallback(async (opp) => {
    const key = opp.clientId;
    setCreating(p => ({ ...p, [key]: true }));
    try {
      const res = await fetch(`${BASE}/api/bob/create-task`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: opp.clientId,
          title: `Cross-sell: Recommend ${opp.recommendation?.product} to client`,
          description: opp.recommendation?.reason,
          dueDate: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
        }),
      });
      const json = await res.json();
      if (json.ok) {
        setCreated(p => ({ ...p, [key]: true }));
        if (onTaskCreated) onTaskCreated();
      }
    } catch (_) {}
    setCreating(p => ({ ...p, [key]: false }));
  }, [onTaskCreated]);

  const filtered = (crossSell?.opportunities || []).filter(o => o.score >= minScore);

  return (
    <div className="space-y-4">
      {/* Summary KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="text-center">
          <div className="text-3xl font-bold text-amber-400 tabular-nums">
            {loading ? '—' : fmtN(crossSell?.count || 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">Single-Policy Clients</div>
        </Card>
        <Card className="text-center">
          <div className="text-3xl font-bold text-green-400 tabular-nums">
            {loading ? '—' : fmt$(crossSell?.totalPotentialRevenue || 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">Potential Annual Revenue</div>
        </Card>
        <Card className="text-center">
          <div className="text-3xl font-bold text-blue-400 tabular-nums">
            {loading ? '—' : `${filtered.length}`}
          </div>
          <div className="text-xs text-gray-500 mt-1">Opportunities above {minScore} score</div>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <div className="flex items-center gap-4">
          <label className="text-xs text-gray-400 whitespace-nowrap">Min. Score:</label>
          <input
            type="range" min={0} max={100} step={5}
            value={minScore} onChange={e => setMinScore(Number(e.target.value))}
            className="flex-1 accent-amber-400"
          />
          <span className="text-sm font-bold text-amber-400 w-8 tabular-nums">{minScore}</span>
        </div>
      </Card>

      {/* Opportunity Table */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-300">Ranked Opportunities</h3>
          <span className="text-xs text-gray-500">{filtered.length} clients</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs border-b border-gray-800">
                <th className="text-left py-2 px-2">Score</th>
                <th className="text-left py-2 px-2">Current Plan</th>
                <th className="text-left py-2 px-2">Carrier</th>
                <th className="text-left py-2 px-2">Recommendation</th>
                <th className="text-left py-2 px-2">Est. Commission</th>
                <th className="text-left py-2 px-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? [...Array(6)].map((_, i) => (
                  <tr key={i} className="border-b border-gray-800">
                    {[...Array(6)].map((_, j) => <td key={j} className="py-2 px-2"><Skeleton h="h-4" /></td>)}
                  </tr>
                ))
                : filtered.map(opp => {
                  const scoreColor = opp.score >= 80 ? '#10B981' : opp.score >= 60 ? '#FFB800' : '#9CA3AF';
                  const isCreated  = created[opp.clientId];
                  const isCreating = creating[opp.clientId];
                  return (
                    <tr key={opp.clientId} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="py-2 px-2">
                        <span className="font-bold tabular-nums" style={{ color: scoreColor }}>{opp.score}</span>
                      </td>
                      <td className="py-2 px-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-800 text-gray-300">
                          {opp.currentPlan || 'Unknown'}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-gray-400">{opp.currentCarrier || '—'}</td>
                      <td className="py-2 px-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-400/20 text-amber-400 inline-block w-fit">
                            {opp.recommendation?.product}
                          </span>
                          <span className="text-xs text-gray-500">{opp.recommendation?.reason}</span>
                        </div>
                      </td>
                      <td className="py-2 px-2 tabular-nums text-green-400 font-semibold">
                        {fmt$(opp.estimatedAnnualComm)}/yr
                      </td>
                      <td className="py-2 px-2">
                        <button
                          onClick={() => !isCreated && createTask(opp)}
                          disabled={isCreated || isCreating}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                            isCreated
                              ? 'bg-green-900/40 text-green-400 cursor-default'
                              : 'bg-amber-400 text-gray-950 hover:bg-amber-300 disabled:opacity-60'
                          }`}
                        >
                          {isCreated ? <Check size={12} /> : isCreating ? <RefreshCw size={12} className="animate-spin" /> : <Plus size={12} />}
                          {isCreated ? 'Task Created' : 'Create Task'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              }
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-600 text-sm">
                    No opportunities found above score {minScore}. Try lowering the minimum score.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB 6: BOOK VALUATION
// ────────────────────────────────────────────────────────────────────────────
const SLIDER_DEFAULTS = {
  ppcAdj:        0,   // policies/client adj: -10 to +10 bps
  diversAdj:     0,   // carrier diversification adj
  ageAdj:        0,   // client age profile adj
  geoAdj:        0,   // geographic coverage adj
  renewalAdj:    0,   // renewal rate adj
  growthAdj:     0,   // growth trend adj
};

function TabValuation({ valuation }) {
  const loading = !valuation;
  const [sliders, setSliders] = useState(SLIDER_DEFAULTS);

  const adjustedMultiplier = 3.25 + Object.values(sliders).reduce((s, v) => s + v, 0) / 100;
  const clamped = Math.max(2.0, Math.min(5.0, adjustedMultiplier));
  const adjValuation = Math.round((valuation?.arr || 0) * clamped);

  const SLIDER_DEFS = [
    { key: 'ppcAdj',     label: 'Policies/Client Ratio', hint: 'Higher ratio = higher multiplier', min: -15, max: 15 },
    { key: 'diversAdj',  label: 'Carrier Diversification', hint: 'Lower HHI = higher multiplier',   min: -15, max: 15 },
    { key: 'ageAdj',     label: 'Client Age Profile',     hint: 'Younger book = higher multiplier', min: -10, max: 10 },
    { key: 'geoAdj',     label: 'Geographic Coverage',    hint: 'Multi-state = higher multiplier',  min: -10, max: 10 },
    { key: 'renewalAdj', label: 'Renewal Rate',           hint: 'Higher retention = higher multiplier', min: -20, max: 20 },
    { key: 'growthAdj',  label: 'Growth Trend',           hint: 'Positive growth = higher multiplier',  min: -15, max: 15 },
  ];

  return (
    <div className="space-y-4">
      {/* Valuation Range */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Conservative (2.5×)', value: valuation?.valuation?.low,  color: 'text-gray-300' },
          { label: 'Adjusted Estimate',   value: adjValuation,               color: 'text-amber-400' },
          { label: 'Optimistic (4.0×)',   value: valuation?.valuation?.high, color: 'text-green-400' },
        ].map(v => (
          <Card key={v.label} className="text-center">
            <div className={`text-3xl font-bold tabular-nums ${v.color}`}>
              {loading ? '—' : fmt$(v.value)}
            </div>
            <div className="text-xs text-gray-500 mt-1">{v.label}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ARR Breakdown */}
        <Card>
          <h3 className="text-sm font-semibold text-gray-300 mb-4">Annual Recurring Revenue</h3>
          {loading ? (
            <div className="space-y-3"><Skeleton /><Skeleton /><Skeleton /><Skeleton /></div>
          ) : (
            <div className="space-y-3">
              {[
                { label: 'Total Policies',          value: fmtN(valuation.totalPolicies),  sub: 'active with carrier' },
                { label: 'Initial-Year Policies',   value: fmtN(valuation.initialPolicies),sub: 'full commission rate' },
                { label: 'Renewal-Year Policies',   value: fmtN(valuation.renewalPolicies),sub: '50% commission rate for MA' },
                { label: 'Estimated ARR',           value: fmt$(valuation.arr),            sub: 'annual recurring', big: true },
                { label: 'Commission Maturity',     value: `${valuation.commissionMaturity}%`, sub: 'renewal-year policies' },
              ].map(item => (
                <div key={item.label} className={`flex justify-between items-center py-2 ${item.big ? 'border-t border-gray-800 pt-3' : ''}`}>
                  <div>
                    <div className={`${item.big ? 'text-white font-semibold' : 'text-gray-300'} text-sm`}>{item.label}</div>
                    <div className="text-xs text-gray-600">{item.sub}</div>
                  </div>
                  <div className={`tabular-nums font-bold ${item.big ? 'text-amber-400 text-xl' : 'text-white'}`}>{item.value}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Valuation Adjusters */}
        <Card>
          <h3 className="text-sm font-semibold text-gray-300 mb-1">Valuation Adjusters</h3>
          <p className="text-xs text-gray-600 mb-4">Drag to adjust the valuation multiplier. Current: <strong className="text-white">{clamped.toFixed(2)}×</strong></p>
          <div className="space-y-4">
            {SLIDER_DEFS.map(s => (
              <div key={s.key}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-300">{s.label}</span>
                  <span className={`font-mono font-semibold ${sliders[s.key] > 0 ? 'text-green-400' : sliders[s.key] < 0 ? 'text-red-400' : 'text-gray-500'}`}>
                    {sliders[s.key] > 0 ? '+' : ''}{sliders[s.key]} bps
                  </span>
                </div>
                <input
                  type="range"
                  min={s.min} max={s.max} step={1}
                  value={sliders[s.key]}
                  onChange={e => setSliders(p => ({ ...p, [s.key]: Number(e.target.value) }))}
                  className="w-full accent-amber-400"
                />
                <div className="text-xs text-gray-600">{s.hint}</div>
              </div>
            ))}
          </div>
          <button
            onClick={() => setSliders(SLIDER_DEFAULTS)}
            className="mt-4 text-xs text-gray-500 hover:text-gray-300 underline"
          >
            Reset adjusters
          </button>
        </Card>
      </div>

      {/* 36-Month Commission Projection */}
      <Card>
        <h3 className="text-sm font-semibold text-gray-300 mb-3">36-Month Commission Projection</h3>
        <p className="text-xs text-gray-500 mb-3">Assumes 5% annual churn. Initial-year policies convert to renewal rates at month 13.</p>
        {loading ? <Skeleton h="h-52" /> : (
          valuation.monthlyProjection?.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-12">No projection data. Add clients with carriers.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={valuation.monthlyProjection} margin={{ top: 4, right: 8, bottom: 16, left: 0 }}>
                <defs>
                  <linearGradient id="projGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#FFB800" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#FFB800" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                <XAxis dataKey="month" tick={{ fill: '#9CA3AF', fontSize: 10 }} label={{ value: 'Month', position: 'insideBottom', offset: -8, fill: '#6B7280', fontSize: 10 }} />
                <YAxis tickFormatter={v => fmt$(v)} tick={{ fill: '#9CA3AF', fontSize: 10 }} width={60} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8 }}
                  formatter={(v) => [fmt$(v), 'Monthly Commission']}
                  labelFormatter={l => `Month ${l}`}
                />
                <Area type="monotone" dataKey="commission" stroke="#FFB800" fill="url(#projGrad)" strokeWidth={2} dot={false} />
                {/* Cliff marker at month 13 */}
                <Line type="monotone" dataKey={() => null} />
              </AreaChart>
            </ResponsiveContainer>
          )
        )}
        {!loading && valuation.monthlyProjection?.length > 0 && (
          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
            <Info size={12} className="text-amber-400 shrink-0" />
            Month 13 shows the commission cliff where initial-year MA policies roll to renewal rates (50% reduction).
          </div>
        )}
      </Card>

      {/* Per-policy valuation comparison */}
      <Card>
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Per-Policy Value Range</h3>
        {loading ? <Skeleton h="h-24" /> : (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 w-32">Conservative ($800/policy)</span>
              <div className="flex-1 bg-gray-800 rounded-full h-3">
                <div className="h-3 rounded-full bg-gray-600" style={{ width: '62%' }} />
              </div>
              <span className="text-sm font-bold text-gray-300 tabular-nums">{fmt$(valuation?.valuation?.perPolicyLow)}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 w-32">Optimistic ($1,400/policy)</span>
              <div className="flex-1 bg-gray-800 rounded-full h-3">
                <div className="h-3 rounded-full bg-amber-400" style={{ width: '100%' }} />
              </div>
              <span className="text-sm font-bold text-amber-400 tabular-nums">{fmt$(valuation?.valuation?.perPolicyHigh)}</span>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Per-policy acquisition cost benchmarks: $800 (thin margins) to $1,400 (strong retention, high carrier mix quality).
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// MAIN PAGE COMPONENT
// ────────────────────────────────────────────────────────────────────────────
export default function BookOfBusiness() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // Data state
  const [overview,  setOverview]  = useState(null);
  const [carriers,  setCarriers]  = useState(null);
  const [planMix,   setPlanMix]   = useState(null);
  const [demo,      setDemo]      = useState(null);
  const [t65,       setT65]       = useState(null);
  const [growth,    setGrowth]    = useState(null);
  const [crossSell, setCrossSell] = useState(null);
  const [valuation, setValuation] = useState(null);

  const [loading, setLoading]   = useState(false);
  const [error,   setError]     = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Load overview + carriers always (used in header KPIs)
  const loadCore = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ovRes, carrRes, planRes] = await Promise.all([
        fetch(`${BASE}/api/bob/overview`,              { credentials: 'include' }),
        fetch(`${BASE}/api/bob/carrier-concentration`, { credentials: 'include' }),
        fetch(`${BASE}/api/bob/plan-type-mix`,         { credentials: 'include' }),
      ]);
      const [ovJson, carrJson, planJson] = await Promise.all([ovRes.json(), carrRes.json(), planRes.json()]);
      if (ovJson.ok)   setOverview(ovJson.data);
      if (carrJson.ok) setCarriers(carrJson.data);
      if (planJson.ok) setPlanMix(planJson.data);
      setLastRefresh(new Date());
    } catch (err) {
      setError('Failed to load Book of Business data. Please try again.');
      console.error('[BoB] load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTabData = useCallback(async (tab) => {
    try {
      if (tab === 'demo' && !demo) {
        const [dRes, t65Res] = await Promise.all([
          fetch(`${BASE}/api/bob/demographics`,   { credentials: 'include' }),
          fetch(`${BASE}/api/bob/t65-pipeline`,   { credentials: 'include' }),
        ]);
        const [dJson, t65Json] = await Promise.all([dRes.json(), t65Res.json()]);
        if (dJson.ok)   setDemo(dJson.data);
        if (t65Json.ok) setT65(t65Json.data);
      }
      if (tab === 'growth' && !growth) {
        const gRes = await fetch(`${BASE}/api/bob/growth-trends`, { credentials: 'include' });
        const gJson = await gRes.json();
        if (gJson.ok) setGrowth(gJson.data);
      }
      if (tab === 'crosssell' && !crossSell) {
        const csRes = await fetch(`${BASE}/api/bob/cross-sell-opportunities`, { credentials: 'include' });
        const csJson = await csRes.json();
        if (csJson.ok) setCrossSell(csJson.data);
      }
      if (tab === 'valuation' && !valuation) {
        const vRes = await fetch(`${BASE}/api/bob/valuation`, { credentials: 'include' });
        const vJson = await vRes.json();
        if (vJson.ok) setValuation(vJson.data);
      }
      if ((tab === 'overview' || tab === 'demo') && !t65) {
        const t65Res = await fetch(`${BASE}/api/bob/t65-pipeline`, { credentials: 'include' });
        const t65Json = await t65Res.json();
        if (t65Json.ok) setT65(t65Json.data);
      }
    } catch (err) {
      console.error('[BoB] tab data load error:', err);
    }
  }, [demo, growth, crossSell, valuation, t65]);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab, loadTabData]);

  const handleRefresh = useCallback(async () => {
    setOverview(null); setCarriers(null); setPlanMix(null);
    setDemo(null); setT65(null); setGrowth(null); setCrossSell(null); setValuation(null);
    await loadCore();
    await loadTabData(activeTab);
  }, [loadCore, loadTabData, activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* ─── Page Header ─────────────────────────────────────────────────── */}
      <div className="bg-gray-900 border-b border-gray-800 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Title + back */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-white transition-colors shrink-0"
              >
                <ArrowLeft size={18} />
              </button>
              <div>
                <h1 className="text-lg font-bold text-white">Book of Business</h1>
                <p className="text-xs text-gray-500">
                  {lastRefresh
                    ? `Refreshed ${lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                    : 'Loading…'}
                </p>
              </div>
            </div>

            {/* KPI Capsules */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 flex-shrink-0">
              <KpiCapsule
                label="Total Clients"
                value={fmtN(overview?.kpis?.totalClients)}
                loading={!overview}
              />
              <KpiCapsule
                label="Total Policies"
                value={fmtN(overview?.kpis?.totalPolicies)}
                loading={!overview}
              />
              <KpiCapsule
                label="Est. ARR"
                value={fmt$(overview?.kpis?.arrEstimate)}
                loading={!overview}
                accent
              />
              <KpiCapsule
                label="Policies/Client"
                value={overview?.kpis?.policiesPerClient?.toFixed(2) || '—'}
                sub="target: 1.80"
                loading={!overview}
              />
              <HealthGauge score={overview?.healthScore || 0} loading={!overview} />
            </div>

            {/* Refresh */}
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="shrink-0 p-2 text-gray-400 hover:text-white transition-colors disabled:opacity-40"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-0 overflow-x-auto">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-amber-400 text-amber-400'
                      : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                  }`}
                >
                  <Icon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── Main Content ─────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-4 bg-red-900/40 border border-red-800 rounded-lg flex items-center gap-3">
            <AlertTriangle size={16} className="text-red-400 shrink-0" />
            <span className="text-red-300 text-sm">{error}</span>
            <button onClick={handleRefresh} className="ml-auto text-xs text-red-400 underline hover:text-red-300">Retry</button>
          </div>
        )}

        {activeTab === 'overview' && (
          <TabOverview overview={overview} carriers={carriers} planMix={planMix} t65={t65} />
        )}
        {activeTab === 'carrier' && (
          <TabCarrier carriers={carriers} />
        )}
        {activeTab === 'demo' && (
          <TabDemographics demo={demo} t65={t65} />
        )}
        {activeTab === 'growth' && (
          <TabGrowth growth={growth} />
        )}
        {activeTab === 'crosssell' && (
          <TabCrossSell
            crossSell={crossSell}
            onTaskCreated={() => {
              // Refresh cross-sell to show updated state
            }}
          />
        )}
        {activeTab === 'valuation' && (
          <TabValuation valuation={valuation} />
        )}
      </div>
    </div>
  );
}
